import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { PageHeader, Panel, Loading, StatusBadge } from "@/components/common";
import {
  ShareNetwork, YoutubeLogo, InstagramLogo, FacebookLogo,
  TiktokLogo, XLogo, ShieldCheck, Gear, FilmStrip,
} from "@phosphor-icons/react";

const ICONS = {
  youtube: YoutubeLogo,
  instagram: InstagramLogo,
  facebook: FacebookLogo,
  tiktok: TiktokLogo,
  x: XLogo,
};

export default function Social() {
  const { active } = useStudio();
  const [hub, setHub] = useState(null);
  const [queue, setQueue] = useState(null);

  const load = async () => {
    const [{ data: platforms }, { data: posts }] = await Promise.all([
      api.get("/social-hub/platforms"),
      api.get("/social-hub/queue"),
    ]);
    setHub(platforms);
    setQueue(posts);
  };

  useEffect(() => { load(); }, [active?.id]);

  if (!hub || !queue) return <Loading />;

  return (
    <div>
      <PageHeader
        icon={ShareNetwork}
        title="Social Publishing Hub"
        subtitle={`Run ${active.name} across every social channel from one truthful queue. Connections use official OAuth; social passwords are never stored.`}
        badge={<StatusBadge status={hub.connected ? "LIVE" : "SETUP REQUIRED"} />}
      />

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <Panel className="py-4">
          <div className="text-3xl font-display font-black text-white">{hub.connected}/{hub.total}</div>
          <div className="text-xs text-zinc-500">Verified connections</div>
        </Panel>
        <Panel className="py-4">
          <div className="text-3xl font-display font-black text-white">{queue.count}</div>
          <div className="text-xs text-zinc-500">Items in publishing queue</div>
        </Panel>
        <Panel className="py-4">
          <div className="text-3xl font-display font-black text-[#6FA9A5]">9:16</div>
          <div className="text-xs text-zinc-500">PinPilot master format</div>
        </Panel>
      </div>

      <div className="mb-6 flex items-start gap-3 rounded-xl border border-[#6FA9A5]/25 bg-[#276A6B]/10 px-4 py-3">
        <ShieldCheck size={20} weight="duotone" className="mt-0.5 text-[#8CC5C0]" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-white">No fake publishing</div>
          <p className="mt-1 text-xs leading-relaxed text-zinc-400">{hub.truth}</p>
        </div>
        <Link to="/setup" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-white">
          <Gear /> Setup
        </Link>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {hub.platforms.map((platform) => {
          const Icon = ICONS[platform.id] || ShareNetwork;
          return (
            <Panel key={platform.id} className="flex min-h-44 flex-col">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/5">
                  <Icon size={23} weight="duotone" className="text-[#8CC5C0]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-white">{platform.name}</div>
                  <div className="text-xs text-zinc-500">{platform.destination}</div>
                </div>
                <StatusBadge status={platform.status} />
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {platform.formats.map((format) => (
                  <span key={format} className="rounded-full border border-white/8 bg-black/20 px-2 py-1 text-[10px] uppercase text-zinc-400">{format}</span>
                ))}
              </div>
              <div className="mt-auto pt-4 text-xs text-zinc-500">
                {platform.status === "CONNECTED"
                  ? `Connected as ${platform.connected_account || "verified account"}`
                  : platform.required_settings.length
                    ? `Needs: ${platform.required_settings.join(", ")}`
                    : "Credentials configured; OAuth authorization is next."}
              </div>
            </Panel>
          );
        })}
      </div>

      <Panel className="mt-6">
        <div className="mb-3 flex items-center gap-2">
          <FilmStrip className="text-[#B88746]" weight="fill" />
          <h3 className="font-display font-bold text-white">Publishing queue</h3>
          <Link to="/publisher" className="ml-auto text-xs text-[#8CC5C0]">Prepare a post →</Link>
        </div>
        {queue.posts.length === 0 ? (
          <p className="text-sm text-zinc-500">No posts prepared yet. Choose a real clip in Publisher to start.</p>
        ) : (
          <div className="space-y-2">
            {queue.posts.slice(0, 8).map((post) => (
              <div key={post.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/8 bg-black/20 p-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-white">{post.title}</div>
                  <div className="mt-1 text-xs text-zinc-500">{post.platforms.join(" · ")}{post.scheduled_for ? ` · ${post.scheduled_for}` : ""}</div>
                </div>
                <StatusBadge status={post.status} />
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
